import { Injectable } from '@angular/core';
import { db, ChatMessage, CachedRoom } from '../database/db';

@Injectable({
    providedIn: 'root'
}) export class ChatStorageService {

    async addMessage(message: Omit<ChatMessage, 'id'>) {
        if (!this.isStringKey(message.chatId)) return undefined;

        const eventId = this.isStringKey(message.eventId) ? message.eventId : undefined;
        const normalizedMessage = {
            ...message,
            eventId,
            timestamp: Number.isFinite(message.timestamp) ? message.timestamp : Date.now()
        };

        return db.transaction('rw', db.messages, async () => {
            if (eventId) {
                const existing = await db.messages.where('eventId').equals(eventId).first();
                if (existing?.id !== undefined) {
                    await db.messages.update(existing.id, normalizedMessage);
                    return existing.id;
                }
            }
            return db.messages.add(normalizedMessage);
        });
    }

    async getMessages(chatId: string) {
        if (!this.isStringKey(chatId)) return [];
        return await db.messages
            .where('chatId')
            .equals(chatId)
            .sortBy('timestamp');
    }

    async deleteMessageByEventId(eventId: string) {
        if (!this.isStringKey(eventId)) return;
        await db.messages.where('eventId').equals(eventId).delete();
    }

    async replaceMessageEventId(oldEventId: string, newEventId: string) {
        if (!this.isStringKey(oldEventId) || !this.isStringKey(newEventId)) return;

        await db.transaction('rw', db.messages, async () => {
            const oldMessage = await db.messages.where('eventId').equals(oldEventId).first();
            if (oldMessage?.id === undefined) return;

            const newMessage = await db.messages.where('eventId').equals(newEventId).first();
            if (newMessage?.id !== undefined && newMessage.id !== oldMessage.id) {
                await db.messages.delete(oldMessage.id);
                return;
            }

            await db.messages.update(oldMessage.id, {eventId: newEventId});
        });
    }

    async deleteLocalEchoMessages() {
        await db.messages.where('eventId').startsWith('~').delete();
    }

    async saveRooms(rooms: CachedRoom[]) {
        const validRooms = rooms
            .filter(room => this.isStringKey(room.roomId))
            .map(room => ({
                ...room,
                lastEvent: Number.isFinite(room.lastEvent) ? room.lastEvent : 0
            }));
        if (validRooms.length) await db.rooms.bulkPut(validRooms);
    }

    async getRooms() {
        return db.rooms.orderBy('lastEvent').reverse().toArray();
    }

    async saveImage(mediaId: string, blob: Blob) {
        if (!this.isStringKey(mediaId)) return;
        await db.images.put({mediaId, blob, cachedAt: Date.now()});
    }

    async getImage(mediaId: string) {
        if (!this.isStringKey(mediaId)) return undefined;
        return db.images.get(mediaId);
    }

    async saveLastOpenedRoomId(roomId: string) {
        await db.preferences.put({key: 'lastOpenedRoomId', value: roomId});
    }

    async getLastOpenedRoomId() {
        return db.preferences.get('lastOpenedRoomId');
    }

    private isStringKey(value: unknown): value is string {
        return typeof value === 'string' && value.length > 0;
    }

    async markMessageAsDeleted(eventId: string) {
    if (!this.isStringKey(eventId)) return;

    const message = await db.messages
        .where('eventId')
        .equals(eventId)
        .first();

    if (message?.id === undefined) return;

    await db.messages.update(message.id, {
        deleted: true,
        text: '',
        filename: '',
        url: undefined,
        info: undefined,
    });
}
}
